import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, money, money0, shortDate, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusPill, useBackdropDismiss } from "../components/UI";
import { useAuth } from "../lib/auth";

function EditMachineDialog({ machine, onClose, onSaved }) {
  const initial = {
    // --- identity + whereabouts (added Sep 2026 for ACH512 corrections) ---
    // These live at the top of the dialog because they answer "what is this
    // machine and where is it right now?" — the two questions Todd's ACH512
    // fix was actually about. `unit_number` and `owning_company` are
    // deliberately not editable here.
    make: machine.make || "",
    model_name: machine.model_name || "",
    serial: machine.serial || "",
    capacity_tonnes: machine.capacity_tonnes ?? "",
    location: machine.location || "",
    // --- existing admin fields (rego, inspections, notes, …) ---
    rego: machine.rego || "",
    rego_expiry: machine.rego_expiry || "",
    cranesafe_due: machine.cranesafe_due || "",
    cranesafe_sign_off_date: machine.cranesafe_sign_off_date || "",
    ten_year_due: machine.ten_year_due || "",
    plant_reg: machine.plant_reg || "",
    plant_reg_expiry: machine.plant_reg_expiry || "",
    lease_payout: machine.lease_payout ?? "",
    home_yard: machine.home_yard || "",
    internal_notes: machine.internal_notes || "",
    service_notes: machine.service_notes || "",
    hire_notes: machine.hire_notes || "",
    key_location: machine.key_location || "",
    accessories: machine.accessories || "",
    folder_link: machine.folder_link || "",
  };
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF({ ...f, [k]: v });

  // Dirty if any field diverges from the value we opened with. Used to protect
  // against silent data loss when Todd brushes past the edge of the dialog.
  const isDirty = Object.keys(initial).some(k => String(f[k] ?? "") !== String(initial[k] ?? ""));
  const dismiss = useBackdropDismiss(isDirty, onClose);

  const requestClose = () => {
    if (isDirty && !window.confirm("Discard the changes you just typed?")) return;
    onClose();
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {};
      Object.keys(f).forEach(k => {
        const v = f[k];
        if (v === "" || v === null) return;
        if (k === "lease_payout" || k === "capacity_tonnes") payload[k] = Number(v);
        else payload[k] = v;
      });
      await api.patch(`/machines/${machine.id}`, payload);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="edit-machine-dialog" {...dismiss}>
      <div className="ach-modal" style={{maxWidth: 640}} onMouseDown={(e)=>e.stopPropagation()} onMouseUp={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>Edit details — {machine.unit_number}</h2></div>
        <div className="ach-modal__body">
          <p style={{margin:"0 0 12px", fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5}}>
            Use this when a rego is renewed, a CraneSafe or 10-year inspection is booked, a plant registration is renewed, the machine moves to a different home yard, or you need to correct the lease payout figure. You can also fix imported details — make, model, serial, capacity — and update where the machine is right now.
          </p>
          <div className="ach-form-grid">
            <div className="ach-field">
              <label>Make</label>
              <input value={f.make} onChange={(e)=>set("make", e.target.value)} data-testid="em-make" placeholder="e.g. Franna" />
            </div>
            <div className="ach-field">
              <label>Model</label>
              <input value={f.model_name} onChange={(e)=>set("model_name", e.target.value)} data-testid="em-model" placeholder="e.g. AT40" />
            </div>
            <div className="ach-field">
              <label>Serial number</label>
              <input value={f.serial} onChange={(e)=>set("serial", e.target.value)} data-testid="em-serial" placeholder="Manufacturer serial" />
            </div>
            <div className="ach-field">
              <label>Capacity (tonnes)</label>
              <input type="number" min="0" step="0.5" value={f.capacity_tonnes} onChange={(e)=>set("capacity_tonnes", e.target.value)} data-testid="em-capacity" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Where it is now</label>
              <input value={f.location} onChange={(e)=>set("location", e.target.value)} placeholder="e.g. John Holland — Kingsford Smith site" data-testid="em-location" />
              <div className="helper">Physical whereabouts today — customer or site. Separate from the home yard below.</div>
            </div>
            <div className="ach-field">
              <label>Rego number</label>
              <input value={f.rego} onChange={(e)=>set("rego", e.target.value)} data-testid="em-rego" />
            </div>
            <div className="ach-field">
              <label>Rego expires</label>
              <input type="date" value={f.rego_expiry || ""} onChange={(e)=>set("rego_expiry", e.target.value)} data-testid="em-rego-expiry" />
            </div>
            <div className="ach-field">
              <label>CraneSafe due</label>
              <input type="date" value={f.cranesafe_due || ""} onChange={(e)=>set("cranesafe_due", e.target.value)} data-testid="em-cs-due" />
            </div>
            <div className="ach-field">
              <label>CraneSafe sign-off date</label>
              <input type="date" value={f.cranesafe_sign_off_date || ""} onChange={(e)=>set("cranesafe_sign_off_date", e.target.value)} data-testid="em-cs-sign-off" />
              <div className="helper">Filling this in auto-sets the next CraneSafe due date to 12 months later.</div>
            </div>
            <div className="ach-field">
              <label>10-year major due</label>
              <input type="date" value={f.ten_year_due || ""} onChange={(e)=>set("ten_year_due", e.target.value)} data-testid="em-ten-due" />
            </div>
            <div className="ach-field">
              <label>Plant registration #</label>
              <input value={f.plant_reg || ""} onChange={(e)=>set("plant_reg", e.target.value)} data-testid="em-plant-reg" placeholder="e.g. PR-12345" />
            </div>
            <div className="ach-field">
              <label>Plant reg expiry</label>
              <input type="date" value={f.plant_reg_expiry || ""} onChange={(e)=>set("plant_reg_expiry", e.target.value)} data-testid="em-plant-reg-expiry" />
            </div>
            <div className="ach-field">
              <label>Lease payout ($)</label>
              <input type="number" min="0" step="500" value={f.lease_payout} onChange={(e)=>set("lease_payout", e.target.value)} data-testid="em-payout" placeholder="Leave blank if not on lease" />
            </div>
            <div className="ach-field">
              <label>Home yard</label>
              <input value={f.home_yard} onChange={(e)=>set("home_yard", e.target.value)} placeholder="e.g. Yard — Newcastle" data-testid="em-home-yard" />
              <div className="helper">Where the machine returns to when off-hired.</div>
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Key location</label>
              <input value={f.key_location} onChange={(e)=>set("key_location", e.target.value)} placeholder="e.g. Key #14 on the yard board" data-testid="em-key-location" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Accessories &amp; attachments</label>
              <input value={f.accessories} onChange={(e)=>set("accessories", e.target.value)} placeholder="e.g. jib + fly, spreader bar, 2x lifting chains" data-testid="em-accessories" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Shared folder link</label>
              <input value={f.folder_link} onChange={(e)=>set("folder_link", e.target.value)} placeholder="https://... (SharePoint / Drive / Dropbox — paste the folder URL)" data-testid="em-folder-link" />
              <div className="helper">Optional — when set, a small link icon appears on the Machines list so you can open the folder in one click.</div>
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Service notes</label>
              <textarea rows={2} value={f.service_notes} onChange={(e)=>set("service_notes", e.target.value)} placeholder="Anything the mechanic should know" data-testid="em-service-notes" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Hire notes</label>
              <textarea rows={2} value={f.hire_notes} onChange={(e)=>set("hire_notes", e.target.value)} placeholder="Anything the yard / dispatcher should know when sending it out" data-testid="em-hire-notes" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Internal notes</label>
              <textarea rows={2} value={f.internal_notes} onChange={(e)=>set("internal_notes", e.target.value)} placeholder="Private — not shown to customers" data-testid="em-internal-notes" />
            </div>
            {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={requestClose} data-testid="em-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy} data-testid="em-save">
            {busy ? "Saving…" : "Save changes →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MachineDetails() {
  const [machines, setMachines] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [machine, setMachine] = useState(null);
  const [hires, setHires] = useState([]);
  const [customers, setCustomers] = useState({});
  const [editing, setEditing] = useState(false);
  const [flash, setFlash] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const canEdit = user?.role === "Director" || user?.role === "Office";

  const loadMachine = () => {
    if (!selectedId) return;
    api.get(`/machines/${selectedId}`).then(r => setMachine(r.data));
    api.get(`/hires`).then(r => setHires(r.data.filter(h => h.machine_id === selectedId)));
  };

  useEffect(() => {
    Promise.all([api.get("/machines"), api.get("/customers")]).then(([m, c]) => {
      setMachines(m.data);
      const map = {};
      c.data.forEach(x => { map[x.id] = x; });
      setCustomers(map);
      const wantedUnit = searchParams.get("unit");
      const targeted = wantedUnit ? m.data.find(x => x.unit_number === wantedUnit) : null;
      if (targeted) setSelectedId(targeted.id);
      else if (m.data.length > 0) setSelectedId(m.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadMachine(); }, [selectedId]);

  if (machines.length === 0) {
    return (
      <div data-testid="machine-details-page">
        <PageHeader title="Machine details" subtitle="Everything about one machine — in one place" />
        <div className="ach-card" data-testid="md-empty" style={{textAlign:"center", padding:"40px 20px"}}>
          <h2 className="ach-card__title" style={{marginBottom:8}}>No machines in the fleet yet</h2>
          <p style={{fontSize:13, color:"var(--ach-text-2)", margin:"0 auto 16px", maxWidth:460, lineHeight:1.5}}>
            Once machines are added — either by bulk-importing your fleet CSV, or one at a time — you&apos;ll be able to pick any of them here to see rego, inspections, hire history and current status.
          </p>
          {user?.role === "Director" && (
            <button className="ach-btn ach-btn--primary" onClick={()=>nav("/settings/bulk-import")} data-testid="md-goto-import">Bulk import machines →</button>
          )}
        </div>
      </div>
    );
  }

  if (!machine) return <div style={{padding: 40, color:"var(--ach-text-2)"}}>Loading…</div>;

  const activeHire = hires.find(h => h.status === "active");
  const currentCustomer = activeHire ? customers[activeHire.customer_id] : null;
  const monthly = activeHire ? activeHire.weekly_rate * 4.33 : 0;

  return (
    <div data-testid="machine-details-page">
      <PageHeader
        title="Machine details"
        subtitle="Everything about one machine — in one place"
        actions={
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <select value={selectedId} onChange={(e)=>setSelectedId(e.target.value)}
              data-testid="pick-machine-detail"
              style={{border:"1px solid var(--ach-card-border)", borderRadius: 6, padding:"8px 10px", fontSize: 13, background:"#fff"}}>
              {machines.map(m => {
                const label = `${m.unit_number} · ${m.make} ${m.model_name}`;
                return <option key={m.id} value={m.id}>{label}</option>;
              })}
            </select>
            {canEdit && (
              <button className="ach-btn ach-btn--primary" onClick={()=>setEditing(true)} data-testid="btn-edit-machine">Edit details</button>
            )}
          </div>
        }
      />

      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap: 16, marginBottom: 16}}>
        <div className="ach-card">
          <h2 className="ach-card__title">The machine</h2>
          <table className="ach-table">
            <tbody>
              <tr><td style={{color:"var(--ach-text-2)", width: 160}}>Unit number</td><td><strong>{machine.unit_number}</strong></td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Make &amp; model</td><td>{machine.make} {machine.model_name}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Capacity</td><td>{machine.capacity_tonnes}t</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Serial</td><td>{machine.serial}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Owned by</td><td>{machine.owning_company}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Where it is now</td><td>{machine.location || "—"}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Home yard</td><td>{machine.home_yard || <em style={{color:"var(--ach-text-2)"}}>not set</em>}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Status</td><td><StatusPill tone={machine.status === "on-hire" ? "green" : machine.status === "free" ? "neutral" : "blue"}>{machine.status.replace(/-/g, " ").toUpperCase()}</StatusPill></td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Lease payout</td><td>{machine.lease_payout ? money(machine.lease_payout) : "—"}</td></tr>
              <tr>
                <td style={{color:"var(--ach-text-2)"}}>Shared folder</td>
                <td data-testid="md-folder-link-cell">
                  {machine.folder_link
                    ? <a href={machine.folder_link} target="_blank" rel="noopener noreferrer" data-testid="md-folder-link" style={{color:"var(--ach-maroon)", textDecoration:"underline", wordBreak:"break-all"}}>
                        {machine.folder_link}
                      </a>
                    : <em style={{color:"var(--ach-text-2)"}}>not set</em>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="ach-card">
          <h2 className="ach-card__title">Papers &amp; inspections</h2>
          <table className="ach-table">
            <tbody>
              <tr><td style={{color:"var(--ach-text-2)", width: 200}}>Rego number</td><td>{machine.rego || "—"}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Rego expires</td><td>{shortDate(machine.rego_expiry)}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>CraneSafe due</td><td>{shortDate(machine.cranesafe_due)}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>10-year major due</td><td>{shortDate(machine.ten_year_due)}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Plant reg #</td><td>{machine.plant_reg || "—"}</td></tr>
              <tr><td style={{color:"var(--ach-text-2)"}}>Plant reg expires</td><td>{shortDate(machine.plant_reg_expiry)}</td></tr>
            </tbody>
          </table>
          {activeHire && (
            <>
              <h2 className="ach-card__title" style={{marginTop: 16}}>Out on hire right now</h2>
              <table className="ach-table">
                <tbody>
                  <tr><td style={{color:"var(--ach-text-2)", width: 200}}>Customer</td><td>{currentCustomer?.legal_name || "—"}</td></tr>
                  <tr><td style={{color:"var(--ach-text-2)"}}>Site</td><td>{activeHire.site_location || "—"}</td></tr>
                  <tr><td style={{color:"var(--ach-text-2)"}}>Since</td><td>{shortDate(activeHire.start_date)}</td></tr>
                  <tr><td style={{color:"var(--ach-text-2)"}}>Expected back</td><td>{shortDate(activeHire.expected_end_date)}</td></tr>
                  <tr><td style={{color:"var(--ach-text-2)"}}>Weekly / Monthly</td><td>{money(activeHire.weekly_rate)} · <strong>{money0(monthly)}</strong>/mth</td></tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      <div className="ach-card" data-testid="machine-notes" style={{marginBottom:16}}>
        <h2 className="ach-card__title">Notes on this machine</h2>
        <table className="ach-table">
          <tbody>
            <tr><td style={{color:"var(--ach-text-2)", width: 200}}>Key location</td><td>{machine.key_location || <em style={{color:"var(--ach-text-2)"}}>not set</em>}</td></tr>
            <tr><td style={{color:"var(--ach-text-2)"}}>Accessories</td><td>{machine.accessories || <em style={{color:"var(--ach-text-2)"}}>—</em>}</td></tr>
            <tr><td style={{color:"var(--ach-text-2)"}}>Service notes</td><td style={{whiteSpace:"pre-wrap"}}>{machine.service_notes || <em style={{color:"var(--ach-text-2)"}}>—</em>}</td></tr>
            <tr><td style={{color:"var(--ach-text-2)"}}>Hire notes</td><td style={{whiteSpace:"pre-wrap"}}>{machine.hire_notes || <em style={{color:"var(--ach-text-2)"}}>—</em>}</td></tr>
            <tr><td style={{color:"var(--ach-text-2)"}}>Internal notes</td><td style={{whiteSpace:"pre-wrap"}}>{machine.internal_notes || <em style={{color:"var(--ach-text-2)"}}>—</em>}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="ach-card" data-testid="machine-hire-history">
        <h2 className="ach-card__title">Hire history for {machine.unit_number}</h2>
        <div className="ach-table-wrap">
          <table className="ach-table">
            <thead>
              <tr>
                <th>Customer</th><th>Started</th><th>Ended</th><th>Weekly rate</th><th>How it was billed</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {hires.map(h => (
                <tr key={h.id}
                    data-testid={`md-hire-row-${h.id}`}
                    onClick={()=>nav(`/hires?highlight=${h.id}`)}
                    style={{cursor:"pointer"}}
                    title="Open this hire">
                  <td>{customers[h.customer_id]?.legal_name}</td>
                  <td>{shortDate(h.start_date)}</td>
                  <td>{h.actual_end_date ? shortDate(h.actual_end_date) : (h.status === "active" ? "— still out —" : "—")}</td>
                  <td>{money(h.weekly_rate)}</td>
                  <td><StatusPill tone="neutral">{h.billing_style.replace(/-/g, " ").toUpperCase()}</StatusPill></td>
                  <td><StatusPill tone={h.status === "active" ? "green" : "neutral"}>{h.status.toUpperCase()}</StatusPill></td>
                </tr>
              ))}
              {hires.length === 0 && (
                <tr><td colSpan={6} style={{padding: 20, textAlign: "center", color:"var(--ach-text-2)"}}>No hire history yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditMachineDialog machine={machine} onClose={()=>setEditing(false)}
        onSaved={()=>{ setFlash("Machine details saved."); setTimeout(()=>setFlash(null), 4000); loadMachine(); }} />}

      {flash && (
        <div data-testid="md-flash"
          style={{position:"fixed", right:20, bottom:20, zIndex:1000, padding:"10px 14px", borderRadius:8,
            background:"var(--ach-green-tint)", color:"var(--ach-green)",
            border:"1px solid var(--ach-card-border)", fontSize:13, fontWeight:500, maxWidth:360, boxShadow:"0 4px 16px rgba(0,0,0,0.08)"}}>
          {flash}
        </div>
      )}
    </div>
  );
}
